import { useState, useEffect } from "react";
import { Monitor, Plus, RefreshCw, CheckCircle, XCircle, AlertTriangle, Cpu, Activity, ArrowLeftRight, RotateCcw, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import { FALLBACK_DEVICES } from "@/data/dummyData";

export default function SmartPayEquipment() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("devices");
  const [devices, setDevices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);

  const [showNewDevice, setShowNewDevice] = useState(false);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignDeviceId, setAssignDeviceId] = useState("");
  const [assignTarget, setAssignTarget] = useState("");
  const [assignType, setAssignType] = useState("employee");
  const [predictResult, setPredictResult] = useState(null);
  const [predictDeviceId, setPredictDeviceId] = useState("");

  const [newDevice, setNewDevice] = useState({ name: "", device_type: "LAPTOP", brand: "", model: "", serial_number: "", purchase_date: "", warranty_expiry: "" });
  const [newRequest, setNewRequest] = useState({ employee_name: "", department: "", device_type: "LAPTOP", priority: "MEDIUM", reason: "" });
  const [healthData, setHealthData] = useState({ device_id: "", cpu_usage: 0, memory_usage: 0, disk_usage: 0, battery_health: 100, temperature: 0 });

  const fetchDevices = async () => { try { const res = await axios.get("/api/smartpay/devices"); const d = res.data.devices || []; setDevices(d.length > 0 ? d : FALLBACK_DEVICES); } catch { setDevices(FALLBACK_DEVICES); } };
  const fetchRequests = async () => { try { const res = await axios.get("/api/smartpay/device-requests"); setRequests(res.data.requests || []); } catch {} };
  const fetchAlerts = async () => { try { const res = await axios.get("/api/smartpay/devices/ai/maintenance-alerts"); setMaintenanceAlerts(res.data.alerts || []); } catch {} };

  useEffect(() => { fetchDevices(); fetchRequests(); fetchAlerts(); }, []);

  const handleCreateDevice = async () => {
    try { await axios.post("/api/smartpay/devices", newDevice); toast({ title: "Device created" }); setShowNewDevice(false); fetchDevices(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateRequest = async () => {
    try { await axios.post("/api/smartpay/device-requests", newRequest); toast({ title: "Request submitted" }); setShowNewRequest(false); fetchRequests(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleAssign = async () => {
    if (!assignDeviceId || !assignTarget) { toast({ title: "Fill all fields", variant: "destructive" }); return; }
    try {
      await axios.post(`/api/smartpay/devices/${assignDeviceId}/assign`, { assign_to: assignTarget, assign_type: assignType });
      toast({ title: "Device assigned" }); setAssignDialog(false); fetchDevices();
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleRecover = async (id) => {
    try { await axios.post(`/api/smartpay/devices/${id}/recover`); toast({ title: "Device recovered" }); fetchDevices(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleRetire = async (id) => {
    try { await axios.post(`/api/smartpay/devices/${id}/retire`); toast({ title: "Device retired" }); fetchDevices(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleApproveRequest = async (id) => {
    try { await axios.post(`/api/smartpay/device-requests/${id}/approve`); toast({ title: "Request approved" }); fetchRequests(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleRejectRequest = async (id) => {
    try { await axios.post(`/api/smartpay/device-requests/${id}/reject`); toast({ title: "Request rejected" }); fetchRequests(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleFulfill = async (id) => {
    try { await axios.post(`/api/smartpay/device-requests/${id}/fulfill`); toast({ title: "Request fulfilled" }); fetchRequests(); fetchDevices(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleRecordHealth = async () => {
    try { await axios.post(`/api/smartpay/devices/${healthData.device_id}/health`, healthData); toast({ title: "Health recorded" }); fetchDevices(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handlePredictFailure = async (id) => {
    try { const res = await axios.post(`/api/smartpay/devices/${id}/predict-failure`); setPredictResult(res.data); setPredictDeviceId(id); toast({ title: "AI prediction ready" }); } catch { toast({ title: "Prediction failed", variant: "destructive" }); }
  };

  const lifecycleColor = (stage) => {
    const map = { ACTIVE: "default", IN_MAINTENANCE: "warning", RETIRED: "destructive", STORAGE: "secondary" };
    return map[stage] || "secondary";
  };

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold">Equipment Management</h1><p className="text-muted-foreground">Devices, requests, lifecycle & AI insights</p></div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="lifecycle">Lifecycle</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="devices" className="mt-4">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold">Device Catalog</h2>
            <Dialog open={showNewDevice} onOpenChange={setShowNewDevice}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Device</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Device</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Device Name</Label><Input value={newDevice.name} onChange={e => setNewDevice(p => ({ ...p, name: e.target.value }))} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Type</Label><Select value={newDevice.device_type} onValueChange={v => setNewDevice(p => ({ ...p, device_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="LAPTOP">Laptop</SelectItem><SelectItem value="DESKTOP">Desktop</SelectItem><SelectItem value="MONITOR">Monitor</SelectItem><SelectItem value="TABLET">Tablet</SelectItem><SelectItem value="PHONE">Phone</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
                    </Select></div>
                    <div><Label>Brand</Label><Input value={newDevice.brand} onChange={e => setNewDevice(p => ({ ...p, brand: e.target.value }))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Model</Label><Input value={newDevice.model} onChange={e => setNewDevice(p => ({ ...p, model: e.target.value }))} /></div>
                    <div><Label>Serial #</Label><Input value={newDevice.serial_number} onChange={e => setNewDevice(p => ({ ...p, serial_number: e.target.value }))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Purchase Date</Label><Input type="date" value={newDevice.purchase_date} onChange={e => setNewDevice(p => ({ ...p, purchase_date: e.target.value }))} /></div>
                    <div><Label>Warranty Until</Label><Input type="date" value={newDevice.warranty_expiry} onChange={e => setNewDevice(p => ({ ...p, warranty_expiry: e.target.value }))} /></div>
                  </div>
                  <Button onClick={handleCreateDevice} className="w-full">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Brand/Model</TableHead><TableHead>Assigned To</TableHead><TableHead>Lifecycle</TableHead><TableHead>Health</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {devices.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell><Badge variant="outline">{d.device_type}</Badge></TableCell>
                    <TableCell className="text-sm">{d.brand} {d.model}</TableCell>
                    <TableCell className="text-sm">{d.assigned_to_name || d.assigned_to || "-"}</TableCell>
                    <TableCell><Badge variant={lifecycleColor(d.lifecycle_stage)}>{d.lifecycle_stage}</Badge></TableCell>
                    <TableCell>
                      {d.health_score != null ? (
                        <span className={d.health_score >= 80 ? "text-green-600" : d.health_score >= 50 ? "text-amber-600" : "text-red-600"}>
                          {d.health_score}%
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setAssignDeviceId(d.id); setAssignDialog(true); }} disabled={d.lifecycle_stage !== "ACTIVE"}><ArrowLeftRight className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRecover(d.id)} disabled={d.lifecycle_stage !== "ACTIVE"}><RotateCcw className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRetire(d.id)} disabled={d.lifecycle_stage === "RETIRED"}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {devices.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No devices</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-4">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold">Device Requests</h2>
            <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Request</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Device Request</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Employee Name</Label><Input value={newRequest.employee_name} onChange={e => setNewRequest(p => ({ ...p, employee_name: e.target.value }))} /></div>
                  <div><Label>Department</Label><Input value={newRequest.department} onChange={e => setNewRequest(p => ({ ...p, department: e.target.value }))} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Device Type</Label><Select value={newRequest.device_type} onValueChange={v => setNewRequest(p => ({ ...p, device_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="LAPTOP">Laptop</SelectItem><SelectItem value="DESKTOP">Desktop</SelectItem><SelectItem value="MONITOR">Monitor</SelectItem><SelectItem value="TABLET">Tablet</SelectItem><SelectItem value="PHONE">Phone</SelectItem></SelectContent>
                    </Select></div>
                    <div><Label>Priority</Label><Select value={newRequest.priority} onValueChange={v => setNewRequest(p => ({ ...p, priority: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="LOW">Low</SelectItem><SelectItem value="MEDIUM">Medium</SelectItem><SelectItem value="HIGH">High</SelectItem></SelectContent>
                    </Select></div>
                  </div>
                  <div><Label>Reason</Label><Textarea value={newRequest.reason} onChange={e => setNewRequest(p => ({ ...p, reason: e.target.value }))} /></div>
                  <Button onClick={handleCreateRequest} className="w-full">Submit</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Device</TableHead><TableHead>Department</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {requests.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.employee_name}</TableCell>
                    <TableCell><Badge variant="outline">{r.device_type}</Badge></TableCell>
                    <TableCell className="text-sm">{r.department}</TableCell>
                    <TableCell>
                      <Badge variant={r.priority === "HIGH" ? "destructive" : r.priority === "MEDIUM" ? "warning" : "secondary"}>{r.priority}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.status === "APPROVED" ? "default" : r.status === "FULFILLED" ? "default" : r.status === "REJECTED" ? "destructive" : "warning"}>{r.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {r.status === "PENDING" && (
                          <>
                            <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleApproveRequest(r.id)}><CheckCircle className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRejectRequest(r.id)}><XCircle className="h-4 w-4" /></Button>
                          </>
                        )}
                        {r.status === "APPROVED" && (
                          <Button size="sm" variant="outline" onClick={() => handleFulfill(r.id)}>Fulfill</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {requests.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No requests</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="lifecycle" className="mt-4">
          <h2 className="text-lg font-semibold mb-4">Device Lifecycle Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2"><ArrowLeftRight className="h-5 w-5 text-blue-600" /><h3 className="font-medium">Assign Device</h3></div>
                <p className="text-sm text-muted-foreground mb-3">Assign an active device to an employee or contractor.</p>
                <div className="space-y-2">
                  <Select value={assignDeviceId} onValueChange={setAssignDeviceId}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select device" /></SelectTrigger>
                    <SelectContent>
                      {devices.filter(d => d.lifecycle_stage === "ACTIVE" && !d.assigned_to).map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name} ({d.device_type})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Assign to (name/ID)" value={assignTarget} onChange={e => setAssignTarget(e.target.value)} className="bg-white" />
                  <div className="flex gap-2">
                    <Select value={assignType} onValueChange={setAssignType}>
                      <SelectTrigger className="bg-white flex-1"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="contractor">Contractor</SelectItem></SelectContent>
                    </Select>
                    <Button size="sm" onClick={handleAssign}>Assign</Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2"><RotateCcw className="h-5 w-5 text-amber-600" /><h3 className="font-medium">Recover Device</h3></div>
                <p className="text-sm text-muted-foreground mb-3">Recover a device from an employee (return to storage).</p>
                <div className="space-y-2">
                  {devices.filter(d => d.lifecycle_stage === "ACTIVE" && d.assigned_to).slice(0, 5).map(d => (
                    <div key={d.id} className="flex items-center justify-between bg-white p-2 rounded">
                      <span className="text-sm">{d.name} → {d.assigned_to_name || d.assigned_to}</span>
                      <Button size="sm" variant="ghost" onClick={() => handleRecover(d.id)}><RotateCcw className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  {devices.filter(d => d.lifecycle_stage === "ACTIVE" && d.assigned_to).length === 0 && (
                    <p className="text-sm text-muted-foreground">No assigned devices</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2"><Trash2 className="h-5 w-5 text-red-600" /><h3 className="font-medium">Retire Device</h3></div>
                <p className="text-sm text-muted-foreground mb-3">Permanently retire a device from service.</p>
                <div className="space-y-2">
                  {devices.filter(d => d.lifecycle_stage !== "RETIRED").slice(0, 5).map(d => (
                    <div key={d.id} className="flex items-center justify-between bg-white p-2 rounded">
                      <span className="text-sm">{d.name} ({d.lifecycle_stage})</span>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRetire(d.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  {devices.length === 0 && <p className="text-sm text-muted-foreground">No devices</p>}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-4">
            <CardHeader><CardTitle className="text-sm">Record Device Health</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
                <div>
                  <Label className="text-xs">Device</Label>
                  <Select value={healthData.device_id} onValueChange={v => setHealthData(p => ({ ...p, device_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{devices.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {["cpu_usage", "memory_usage", "disk_usage"].map(field => (
                  <div key={field}>
                    <Label className="text-xs">{field.replace("_", " ").toUpperCase()}</Label>
                    <Input type="number" min="0" max="100" value={healthData[field]} onChange={e => setHealthData(p => ({ ...p, [field]: parseFloat(e.target.value) || 0 }))} />
                  </div>
                ))}
                <div>
                  <Label className="text-xs">Battery</Label>
                  <Input type="number" min="0" max="100" value={healthData.battery_health} onChange={e => setHealthData(p => ({ ...p, battery_health: parseFloat(e.target.value) || 0 }))} />
                </div>
                <Button size="sm" onClick={handleRecordHealth} disabled={!healthData.device_id}>Record</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="mt-4">
          <h2 className="text-lg font-semibold mb-4">AI Insights & Predictions</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-sm"><Cpu className="h-4 w-4 text-purple-500" /> Predict Device Failure</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Select value={predictDeviceId} onValueChange={setPredictDeviceId}>
                    <SelectTrigger><SelectValue placeholder="Select device" /></SelectTrigger>
                    <SelectContent>{devices.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={() => handlePredictFailure(predictDeviceId)} disabled={!predictDeviceId}><Cpu className="h-4 w-4 mr-2" /> Analyze</Button>
                  {predictResult && predictDeviceId === predictDeviceId && (
                    <Card className="bg-indigo-50 border-indigo-200">
                      <CardContent className="p-3 text-sm space-y-1">
                        <p><span className="font-medium">Risk:</span> <Badge variant={predictResult.failure_risk === "HIGH" ? "destructive" : predictResult.failure_risk === "MEDIUM" ? "warning" : "secondary"}>{predictResult.failure_risk}</Badge></p>
                        <p><span className="font-medium">Score:</span> {predictResult.failure_probability != null ? (predictResult.failure_probability * 100).toFixed(0) + "%" : "-"}</p>
                        <p className="text-xs text-muted-foreground">{predictResult.reasoning}</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2 text-sm"><Activity className="h-4 w-4 text-teal-500" /> Maintenance Alerts</CardTitle></CardHeader>
              <CardContent>
                {maintenanceAlerts.length > 0 ? (
                  <div className="space-y-2">
                    {maintenanceAlerts.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">{a.device_name || a.device_id}</p>
                          <p className="text-xs text-muted-foreground">{a.message || a.alert}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No maintenance alerts. Run AI prediction for insights.</p>
                )}
                <Button size="sm" variant="outline" className="mt-3" onClick={fetchAlerts}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-sm">Device Health Overview</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-green-50"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Healthy (≥80%)</p><p className="text-xl font-bold text-green-600">{devices.filter(d => d.health_score >= 80).length}</p></CardContent></Card>
                <Card className="bg-amber-50"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Warning (50-79%)</p><p className="text-xl font-bold text-amber-600">{devices.filter(d => d.health_score >= 50 && d.health_score < 80).length}</p></CardContent></Card>
                <Card className="bg-red-50"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Critical (&lt;50%)</p><p className="text-xl font-bold text-red-600">{devices.filter(d => d.health_score < 50).length}</p></CardContent></Card>
                <Card className="bg-app"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Unknown</p><p className="text-xl font-bold text-slate-600">{devices.filter(d => d.health_score == null).length}</p></CardContent></Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Device</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Device</Label><p className="text-sm font-medium">{devices.find(d => d.id === assignDeviceId)?.name}</p></div>
            <div><Label>Assign To</Label><Input value={assignTarget} onChange={e => setAssignTarget(e.target.value)} placeholder="Employee or contractor name/ID" /></div>
            <div><Label>Type</Label><Select value={assignType} onValueChange={setAssignType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="contractor">Contractor</SelectItem></SelectContent>
            </Select></div>
            <Button onClick={handleAssign} className="w-full">Assign</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
